import {
    AreaLight,
    CutMesh,
    DimensionGuideBox,
    LodType,
    PlanarCurve,
    ProfileExtrudeMesh,
    GeometryGraphPoint,
    GeometryGraphPointScalar,
    GeometryGraphCurve,
    GeometryGraphMesh,
    PdfSettings,
    TransformMatrix,
    Camera,
    ProceduralMesh,
    StringResolve,
    StoredMesh,
    MeshDecal,
    MaterialReference,
    TemplateGraph,
    TemplateReference,
    Group,
    ConfigGroup,
    ConfigVariant,
    RigidRelation,
    Annotation,
    SceneProperties,
    Render,
    PostProcessRender,
    TemplateInstance,
    HdriLight,
    OverlayMaterialColor,
    DimensionGuides,
    fundamentalTypeForNodeClass,
    valueMarker,
    classHasMarker,
    MathOperator,
    MeshCurve,
    Seam,
    MoveMaterial,
    BackgroundMaterial,
    NodesInput,
    BlendMaterialSlot,
    FontMaterial,
    ExtractMaterialSlot,
    InputNode,
    OutputNode,
    SwitchNode,
    ExportNode,
    LightNode,
    GuideNode,
    GeometryGraphNode,
    getGeometryDimension,
    ImageNode,
    NumberNode,
    ObjectNode,
    TemplateNode,
    isFundamentalLikeNode,
    isFundamentalLikeNodeClass,
} from "@cm/template-nodes"
import {
    Mattress,
    MattressCurve,
    MattressCut,
    MattressReferenceFrame,
    MattressComponentGraph,
    MattressComponentInstance,
    MattressMesh,
    MattressCurveInput,
    MattressReferenceFrameInput,
    MattressComputedCurve,
    MattressComponentGraphGenerator,
    MattressCurveExport,
    MattressReferenceFrameExport,
    MattressCurveOutput,
    MattressReferenceFrameOutput,
    MattressGroup,
    MattressNode,
} from "@cm/mattress-nodes"
import {getRegisteredNode} from "@cm/graph-v3"

const resolveTemplateClass = (nodeClass: string) => {
    try {
        return getRegisteredNode(nodeClass, "template")
    } catch {
        return null
    }
}

// Value classes have no shared base (each extends its FT's node base), so identify them by the value
// role marker carried on the class prototype.
const isFundamentalValueNodeClass = (nodeClass: string) => {
    const NodeCtor = resolveTemplateClass(nodeClass)
    return !!NodeCtor && classHasMarker(NodeCtor, valueMarker)
}

// Broad "value node" for the tree/menu italic (`cm-value-node`): a fundamental-like value that isn't a
// scene object — value literals, IO, switches, and producers (MathOperator, FindMaterial, …), but not
// Meshes/Cameras/etc. Distinct from `isFundamentalValueNodeClass` above, which is only the 4 literal
// Value nodes (→ code icon). Exports/variables carry no marker → not italic.
export const isTemplateValueNode = (node: TemplateNode): boolean => isFundamentalLikeNode(node) && !(node instanceof ObjectNode)
export const isTemplateValueNodeClass = (nodeClass: string): boolean => {
    const NodeCtor = resolveTemplateClass(nodeClass)
    return !!NodeCtor && isFundamentalLikeNodeClass(NodeCtor) && !(NodeCtor.prototype instanceof ObjectNode)
}

export const getNodeIconClass = (nodeClass: string) => {
    if (nodeClass === Camera.getNodeClass()) return "far fa-camera"

    const NodeCtor = resolveTemplateClass(nodeClass)

    if (NodeCtor && NodeCtor.prototype instanceof InputNode) return "far fa-sign-in-alt cm-input-icon"
    if (NodeCtor && NodeCtor.prototype instanceof ExportNode) return "far fa-sign-out-alt cm-export-icon"

    if (nodeClass === ProceduralMesh.getNodeClass()) return "far fa-hexagon"
    if (isFundamentalValueNodeClass(nodeClass)) return "far fa-code"
    if (nodeClass === StringResolve.getNodeClass()) return "far fa-binoculars"

    if (nodeClass === StoredMesh.getNodeClass()) return "far fa-cube"
    if (nodeClass === MeshDecal.getNodeClass()) return "far fa-image"
    if (nodeClass === MeshCurve.getNodeClass()) return "far fa-scribble"
    if (nodeClass === CutMesh.getNodeClass()) return "far fa-scissors"
    if (nodeClass === Seam.getNodeClass()) return "far fa-ellipsis-stroke"
    if (nodeClass === DimensionGuides.getNodeClass()) return "far fa-ruler"
    if (nodeClass === DimensionGuideBox.getNodeClass()) return "far fa-ruler-combined"
    if (nodeClass === MaterialReference.getNodeClass()) return "far fa-game-board-alt cm-material-icon"
    if (nodeClass === AreaLight.getNodeClass()) return "far fa-lightbulb cm-light-icon"
    if (NodeCtor && NodeCtor.prototype instanceof LightNode) return "far fa-lightbulb cm-light-icon"
    if (nodeClass === HdriLight.getNodeClass()) return "far fa-sun cm-light-icon"

    if (NodeCtor && NodeCtor.prototype instanceof OutputNode) return "far fa-sign-out-alt"

    if (NodeCtor && NodeCtor.prototype instanceof SwitchNode) return "fas fa-random cm-group-icon"
    if (nodeClass === Group.getNodeClass()) return "fas fa-folder cm-group-icon"
    if (nodeClass === ConfigGroup.getNodeClass()) return "fas fa-list cm-group-icon"
    if (nodeClass === ConfigVariant.getNodeClass()) return "fas fa-cog cm-group-icon"
    if (nodeClass === TemplateGraph.getNodeClass() || nodeClass === TemplateReference.getNodeClass()) return "fas fa-diagram-project cm-template-icon"
    if (nodeClass === TemplateInstance.getNodeClass()) return "far fa-cube cm-template-instance-icon"

    if (nodeClass === RigidRelation.getNodeClass()) return "far fa-arrow-to-right"
    if (NodeCtor && NodeCtor.prototype instanceof GuideNode) return "far fa-map-marker-alt"
    if (nodeClass === Annotation.getNodeClass()) return "far fa-tag"

    if (nodeClass === SceneProperties.getNodeClass()) return "far fa-browser"
    if (nodeClass === Render.getNodeClass()) return "far fa-image"
    if (nodeClass === PostProcessRender.getNodeClass()) return "far fa-palette"

    if (nodeClass === OverlayMaterialColor.getNodeClass()) return "far fa-circles-overlap"
    if (nodeClass === BlendMaterialSlot.getNodeClass()) return "far fa-layer-group"
    if (nodeClass === FontMaterial.getNodeClass()) return "far fa-font"
    if (nodeClass === ExtractMaterialSlot.getNodeClass()) return "far fa-layer-group"
    if (nodeClass === MoveMaterial.getNodeClass()) return "far fa-arrows"
    if (nodeClass === BackgroundMaterial.getNodeClass()) return "far fa-image"

    if (nodeClass === LodType.getNodeClass()) return "far fa-sliders-simple"

    if (nodeClass === PlanarCurve.getNodeClass()) return "far fa-scribble"
    if (nodeClass === ProfileExtrudeMesh.getNodeClass()) return "far fa-draw-polygon"

    if (NodeCtor && NodeCtor.prototype instanceof GeometryGraphNode) return "far fa-hexagon-nodes"
    if (nodeClass === GeometryGraphPoint.getNodeClass()) return "far fa-ellipsis"
    if (nodeClass === GeometryGraphPointScalar.getNodeClass()) return "far fa-input-numeric"
    if (nodeClass === GeometryGraphCurve.getNodeClass()) return "far fa-scribble"
    if (nodeClass === GeometryGraphMesh.getNodeClass()) return "far fa-cube"

    if (nodeClass === Mattress.getNodeClass()) return "far fa-bed"
    if (nodeClass === MattressCurve.getNodeClass()) return "far fa-scribble"
    if (nodeClass === MattressCut.getNodeClass()) return "far fa-scissors"
    if (nodeClass === MattressReferenceFrame.getNodeClass()) return "far fa-vector-square"
    if (nodeClass === MattressComponentGraph.getNodeClass()) return "far fa-diagram-project"
    if (nodeClass === MattressComponentGraphGenerator.getNodeClass()) return "far fa-diagram-project"
    if (nodeClass === MattressComponentInstance.getNodeClass()) return "far fa-cube"
    if (nodeClass === MattressMesh.getNodeClass()) return "far fa-hexagon"
    if (nodeClass === MattressCurveInput.getNodeClass()) return "far fa-sign-in-alt cm-input-icon"
    if (nodeClass === MattressReferenceFrameInput.getNodeClass()) return "far fa-sign-in-alt cm-input-icon"
    if (nodeClass === MattressComputedCurve.getNodeClass()) return "far fa-scribble"
    if (nodeClass === MattressCurveExport.getNodeClass()) return "far fa-sign-out-alt cm-export-icon"
    if (nodeClass === MattressReferenceFrameExport.getNodeClass()) return "far fa-sign-out-alt cm-export-icon"
    if (nodeClass === MattressCurveOutput.getNodeClass()) return "far fa-sign-out-alt cm-export-icon"
    if (nodeClass === MattressReferenceFrameOutput.getNodeClass()) return "far fa-sign-out-alt cm-export-icon"
    if (nodeClass === MattressGroup.getNodeClass()) return "fas fa-folder cm-group-icon"
    if (NodeCtor && NodeCtor.prototype instanceof MattressNode) return "far fa-bed"

    if (nodeClass === MathOperator.getNodeClass()) return "far fa-input-numeric"
    if (nodeClass === PdfSettings.getNodeClass()) return "far fa-file-pdf"
    if (nodeClass === TransformMatrix.getNodeClass()) return "far fa-expand-arrows-alt"

    if (NodeCtor && NodeCtor.prototype instanceof ImageNode) return "far fa-file-image"
    if (NodeCtor && NodeCtor.prototype instanceof NumberNode) return "far fa-input-numeric"

    return "far fa-circle"
}

export const getNodeIconSecondaryClass = (nodeClass: string) => {
    const NodeCtor = resolveTemplateClass(nodeClass)

    const fundamentalType = fundamentalTypeForNodeClass(nodeClass)
    if (fundamentalType) return fundamentalType.icon
    if (NodesInput.getNodeClass() === nodeClass) return "far fa-list"

    if (NodeCtor && NodeCtor.prototype instanceof GeometryGraphNode) {
        switch (getGeometryDimension(NodeCtor)) {
            case 0:
                return "far fa-ellipsis"
            case 1:
                return "far fa-scribble"
            case 2:
                return "far fa-cube"
        }
        // Dimension-agnostic family members (reduce operators, values) get no secondary icon.
        return null
    }
    if (
        nodeClass === GeometryGraphPoint.getNodeClass() ||
        nodeClass === GeometryGraphPointScalar.getNodeClass() ||
        nodeClass === GeometryGraphCurve.getNodeClass() ||
        nodeClass === GeometryGraphMesh.getNodeClass()
    )
        return "far fa-hexagon-nodes"

    return null
}
